import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { formatCurrency, formatDate } from '../../utils/calculations';
import FeedbackForm from './FeedbackForm';
import { API_ENDPOINTS } from '../../config/api';
import axios from '../../config/axiosConfig';

const OrderHistory = ({
  orderHistory,
  contactNumber,
  onContactNumberChange,
  onLoadHistory,
  loading,
  onEditOrder,
  orderHistoryFetched = false,
}) => {
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [showFeedbackForm, setShowFeedbackForm] = useState(null);
  const [orderFeedbacks, setOrderFeedbacks] = useState({});
  const [checkingFeedback, setCheckingFeedback] = useState({});

  // Only show closed orders in history
  const closedOrders = Array.isArray(orderHistory)
    ? orderHistory.filter(order => order && order.status === 'CLOSED')
    : [];

  const toggleOrderExpansion = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  // Check if feedback exists for orders
  useEffect(() => {
    const checkFeedbacks = async () => {
      if (!closedOrders || closedOrders.length === 0) return;
      
      const feedbackChecks = closedOrders.map(async (order) => {
          try {
            setCheckingFeedback(prev => ({ ...prev, [order._id]: true }));
            const response = await axios.get(API_ENDPOINTS.FEEDBACK_GET, {
              params: { orderId: order._id }
            });
            setOrderFeedbacks(prev => ({
              ...prev,
              [order._id]: response.data && response.data.length > 0 ? response.data[0] : null
            }));
          } catch (error) {
            console.error(`Failed to check feedback for order ${order._id}:`, error);
            setOrderFeedbacks(prev => ({
              ...prev,
              [order._id]: null
            }));
          } finally {
            setCheckingFeedback(prev => ({ ...prev, [order._id]: false }));
          }
        });
      
      await Promise.all(feedbackChecks);
    };

    checkFeedbacks();
  }, [orderHistory]); // keep dependency to react when list changes

  const getStatusColor = (status) => {
    switch (status) {
      case 'PLACED':
        return 'bg-gray-100 text-gray-800';
      case 'ACCEPTED':
        return 'bg-blue-100 text-blue-800';
      case 'PREPARING':
        return 'bg-yellow-100 text-yellow-800';
      case 'READY':
        return 'bg-green-100 text-green-800';
      case 'SERVED':
        return 'bg-purple-100 text-purple-800';
      case 'CLOSED':
        return 'bg-gray-200 text-gray-700';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PLACED':
        return '📝';
      case 'ACCEPTED':
        return '✅';
      case 'PREPARING':
        return '👨‍🍳';
      case 'READY':
        return '🔔';
      case 'SERVED':
        return '🍽️';
      case 'CLOSED':
        return '✔️';
      case 'CANCELLED':
        return '❌';
      default:
        return '📋';
    }
  };

  const getItemStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'bg-gray-100 text-gray-700';
      case 'COOKING':
        return 'bg-orange-100 text-orange-700';
      case 'PREPARING':
        return 'bg-yellow-100 text-yellow-700';
      case 'READY':
        return 'bg-green-100 text-green-700';
      case 'SERVED':
        return 'bg-blue-100 text-blue-700';
      case 'DONE':
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getItemStatusIcon = (status) => {
    switch (status) {
      case 'PENDING':
        return '⏳';
      case 'COOKING':
        return '👨‍🍳';
      case 'PREPARING':
        return '⏰';
      case 'READY':
        return '✅';
      case 'SERVED':
        return '🍽️';
      case 'DONE':
        return '✔️';
      default:
        return '⏳';
    }
  };

  return (
    <div className="space-y-6">
      <motion.div
        className="bg-white rounded-2xl border border-stone-200/90 shadow-sm p-4 sm:p-6"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div className="flex items-center gap-3 mb-5 sm:mb-6">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-xl border border-amber-100" aria-hidden>
            📋
          </div>
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-stone-900 tracking-tight">
              Your Order History
            </h2>
            <p className="text-sm text-stone-500 mt-0.5">
              View past completed orders for your mobile number
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-stone-200 bg-stone-50/60 p-4 sm:p-5 mb-6">
          <label htmlFor="order-history-phone" className="block text-sm font-semibold text-stone-800 mb-1">
            Mobile number
          </label>
          <p className="text-xs text-stone-500 mb-3">
            10-digit number used when you placed orders
          </p>
          <input
            id="order-history-phone"
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            value={contactNumber}
            onChange={(e) => onContactNumberChange(e.target.value.replace(/\D/g, '').slice(0, 10))}
            className="w-full border border-stone-200 bg-white px-4 py-3 rounded-xl text-stone-900 placeholder:text-stone-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-400/50 focus:border-amber-400 transition-all mb-4"
            placeholder="e.g. 9876543210"
            maxLength={10}
          />
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-stretch">
            <motion.button
              type="button"
              onClick={onLoadHistory}
              disabled={loading || !contactNumber || contactNumber.length < 10}
              className="flex-1 min-h-[44px] inline-flex items-center justify-center gap-2 rounded-xl bg-stone-900 px-4 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-stone-800 disabled:cursor-not-allowed disabled:bg-stone-300 disabled:text-stone-500 disabled:shadow-none"
              whileHover={loading || !contactNumber || contactNumber.length < 10 ? {} : { scale: 1.01 }}
              whileTap={loading || !contactNumber || contactNumber.length < 10 ? {} : { scale: 0.99 }}
            >
              {loading ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" aria-hidden />
                  <span>Loading…</span>
                </>
              ) : (
                <>
                  <span aria-hidden>🔍</span>
                  <span>Load history</span>
                </>
              )}
            </motion.button>
            <motion.button
              type="button"
              onClick={onLoadHistory}
              disabled={loading || !contactNumber || contactNumber.length < 10}
              className="min-h-[44px] sm:w-[44px] sm:px-0 inline-flex items-center justify-center gap-2 rounded-xl border-2 border-stone-200 bg-white px-4 py-3 text-sm font-semibold text-stone-700 shadow-sm transition-colors hover:bg-stone-50 hover:border-stone-300 disabled:cursor-not-allowed disabled:border-stone-200 disabled:bg-stone-100 disabled:text-stone-400"
              whileHover={loading || !contactNumber || contactNumber.length < 10 ? {} : { scale: 1.01 }}
              whileTap={loading || !contactNumber || contactNumber.length < 10 ? {} : { scale: 0.99 }}
              title="Refresh list"
              aria-label="Refresh order history"
            >
              <span className={loading ? 'inline-block animate-spin' : ''} aria-hidden>🔄</span>
              <span className="sm:hidden">Refresh</span>
            </motion.button>
          </div>
        </div>

        {closedOrders.length > 0 ? (
          <div className="space-y-3">
            <div className="text-sm text-slate-500 mb-2 flex items-center justify-between">
              <span>Found {closedOrders.length} closed orders</span>
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">
                Most recent first
              </span>
            </div>

            {/* Closed orders list */}
            <div className="space-y-3">
              {closedOrders.map((order, index) => {
                const taxAmount = order.taxAmount || (order.totalAmount * (order.taxPercentage || 0) / 100);
                const serviceAmount = order.serviceAmount || 0;
                const finalAmount = order.finalAmount || (order.totalAmount + taxAmount + serviceAmount);
                const isExpanded = expandedOrder === order._id;
                const tableName = order.tableId?.displayName || order.tableId?.tableName || order.tableId?.tableNumber || 'Unknown Table';

                return (
                  <motion.div
                    key={order._id}
                    className="bg-white rounded-lg shadow-md p-4 border-l-4 border-blue-500 transition-all duration-300"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.06 }}
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div>
                            <div className="flex items-center space-x-2 mb-1">
                              <h3 className="text-base font-bold text-gray-800">
                                Order #{order._id.slice(-6)}
                              </h3>
                              <span className={`px-2 py-0.5 rounded-full text-[12px] font-semibold ${getStatusColor(order.status || 'PLACED')}`}>
                                {getStatusIcon(order.status || 'PLACED')} {order.status || 'PLACED'}
                              </span>
                            </div>
                            <p className="text-gray-600 text-sm leading-snug">{order.customerName}</p>
                            <p className="text-gray-500 text-[11px] mt-0.5">{formatDate(order.orderDate)}</p>
                            <p className="text-blue-600 text-[12px] font-medium mt-0.5">🪑 {tableName}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => toggleOrderExpansion(order._id)}
                            className="inline-flex items-center justify-center rounded-full border border-stone-200 bg-stone-50 px-2 py-1 text-[11px] font-semibold text-stone-600 hover:bg-stone-100"
                            aria-label={isExpanded ? 'Hide order details' : 'Show order details'}
                          >
                            {isExpanded ? 'Hide' : 'Show'}
                          </button>
                        </div>

                        {!isExpanded && (
                          <div className="flex items-center justify-between text-sm text-stone-600 border-t pt-2">
                            <span>{order.items.length} item{order.items.length !== 1 ? 's' : ''}</span>
                            <span className="font-bold text-emerald-600">{formatCurrency(finalAmount)}</span>
                          </div>
                        )}

                        {isExpanded && (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-3">
                          <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
                            <p className="text-[11px] text-slate-500 uppercase tracking-wide">Table</p>
                            <p className="text-base font-bold text-slate-800">{tableName}</p>
                          </div>
                          <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
                            <p className="text-[11px] text-slate-500 uppercase tracking-wide">Items</p>
                            <p className="text-base font-bold text-slate-800">{order.items.length}</p>
                          </div>
                          <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
                            <p className="text-[11px] text-slate-500 uppercase tracking-wide">Subtotal</p>
                            <p className="text-base font-bold text-slate-800">{formatCurrency(order.totalAmount)}</p>
                          </div>
                          <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
                            <p className="text-[11px] text-slate-500 uppercase tracking-wide">Tax</p>
                            <p className="text-base font-bold text-slate-800">{formatCurrency(taxAmount)}</p>
                          </div>
                          <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
                            <p className="text-[11px] text-slate-500 uppercase tracking-wide">Platform Fees</p>
                            <p className="text-base font-bold text-slate-800">{formatCurrency(serviceAmount)}</p>
                          </div>
                          {order.appliedOffer && (
                            <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
                              <p className="text-xs text-orange-600 uppercase tracking-wide">Offer</p>
                              <p className="text-sm font-bold text-orange-800">{order.appliedOffer.title}</p>
                              <p className="text-xs text-orange-600">-{formatCurrency(order.offerDiscount || 0)}</p>
                            </div>
                          )}
                            <div className="bg-emerald-50 rounded-lg p-2.5 border border-emerald-100">
                            <p className="text-[11px] text-slate-500 uppercase tracking-wide">Total</p>
                              <p className="text-lg font-bold text-emerald-600">{formatCurrency(finalAmount)}</p>
                          </div>
                        </div>
                        )}
                      </div>
                      {isExpanded && (
                      <div className="flex flex-col sm:flex-row gap-2 pt-1">
                        {/* {order.status !== 'CLOSED' && order.status !== 'CANCELLED' && (
                          <motion.button
                            onClick={() => onEditOrder(order)}
                            className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-xl font-semibold transition-all duration-300 flex items-center space-x-2"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <span>✏️</span>
                            <span>Edit Order</span>
                          </motion.button>
                        )} */}
                        {order.status === 'CLOSED' && !orderFeedbacks[order._id] && (
                          <motion.button
                            onClick={() => setShowFeedbackForm(order._id)}
                            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-xl font-semibold transition-all duration-300 flex items-center space-x-2"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <span>⭐</span>
                            <span>Give Feedback</span>
                          </motion.button>
                        )}
                        {order.status === 'CLOSED' && orderFeedbacks[order._id] && (
                          <motion.div
                            className="bg-emerald-100 text-emerald-800 px-4 py-2 rounded-xl font-semibold flex items-center space-x-2 border border-emerald-200"
                          >
                            <span>✅</span>
                            <span>Feedback Submitted</span>
                          </motion.div>
                        )}
                      </div>
                      )}
                    </div>

                    {isExpanded && (
                      <motion.div
                        className="mt-5 pt-5 border-t border-slate-200"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <h4 className="text-lg font-bold text-slate-800 mb-3">Order Items</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {order.items.map((item, itemIndex) => {
                            const itemStatus = item.status || 'PENDING';
                            return (
                              <div key={itemIndex} className="bg-slate-50 rounded-lg p-4 flex items-center space-x-3 border border-slate-200">
                                <div className="w-12 h-12 bg-white rounded-lg border border-slate-200 flex items-center justify-center">
                                  <span className="text-2xl">{getItemStatusIcon(itemStatus)}</span>
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center justify-between mb-1">
                                    <h5 className="font-semibold text-slate-800">{item.name || item.itemName}</h5>
                                    <span className={`px-2 py-1 rounded text-xs font-semibold ${getItemStatusColor(itemStatus)}`}>
                                      {itemStatus}
                                    </span>
                                  </div>
                                  <p className="text-slate-500 text-sm">Quantity: {item.quantity}</p>
                                  <p className="text-green-600 font-bold">{formatCurrency(item.price * item.quantity)}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {order.appliedOffer && (
                          <div className="mt-4 bg-orange-50 border border-orange-200 rounded-lg p-4">
                            <h5 className="font-semibold text-orange-800 mb-2 flex items-center space-x-2">
                              <span>🎁</span>
                              <span>Applied Offer Details</span>
                            </h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <p className="text-sm text-orange-700"><strong>Offer:</strong> {order.appliedOffer.title}</p>
                                <p className="text-sm text-orange-700"><strong>Description:</strong> {order.appliedOffer.description}</p>
                              </div>
                              <div>
                                <p className="text-sm text-orange-700"><strong>Discount:</strong> {order.appliedOffer.discountType === 'percentage' ? `${order.appliedOffer.discountValue}%` : `₹${order.appliedOffer.discountValue}`}</p>
                                <p className="text-sm text-green-700"><strong>You Saved:</strong> {formatCurrency(order.offerDiscount || 0)}</p>
                              </div>
                            </div>
                          </div>
                        )}

                        {order.message && (
                          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <h5 className="font-semibold text-gray-800 mb-2">Special Instructions</h5>
                            <p className="text-gray-700">{order.message}</p>
                          </div>
                        )}

                        {/* Show feedback if submitted */}
                        {order.status === 'CLOSED' && orderFeedbacks[order._id] && (
                          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
                            <h5 className="font-semibold text-green-800 mb-2 flex items-center space-x-2">
                              <span>⭐</span>
                              <span>Your Feedback</span>
                            </h5>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              {orderFeedbacks[order._id].foodRating && (
                                <div>
                                  <p className="text-sm text-green-700 font-medium">Food Rating</p>
                                  <div className="flex items-center space-x-1">
                                    {[1, 2, 3, 4, 5].map(star => (
                                      <span key={star} className={star <= orderFeedbacks[order._id].foodRating ? 'text-yellow-400' : 'text-gray-300'}>
                                        ★
                                      </span>
                                    ))}
                                    <span className="ml-2 text-sm text-green-700">({orderFeedbacks[order._id].foodRating}/5)</span>
                                  </div>
                                </div>
                              )}
                              {orderFeedbacks[order._id].staffRating && (
                                <div>
                                  <p className="text-sm text-green-700 font-medium">Staff Rating</p>
                                  <div className="flex items-center space-x-1">
                                    {[1, 2, 3, 4, 5].map(star => (
                                      <span key={star} className={star <= orderFeedbacks[order._id].staffRating ? 'text-yellow-400' : 'text-gray-300'}>
                                        ★
                                      </span>
                                    ))}
                                    <span className="ml-2 text-sm text-green-700">({orderFeedbacks[order._id].staffRating}/5)</span>
                                  </div>
                                </div>
                              )}
                              {orderFeedbacks[order._id].restaurantRating && (
                                <div>
                                  <p className="text-sm text-green-700 font-medium">Restaurant Rating</p>
                                  <div className="flex items-center space-x-1">
                                    {[1, 2, 3, 4, 5].map(star => (
                                      <span key={star} className={star <= orderFeedbacks[order._id].restaurantRating ? 'text-yellow-400' : 'text-gray-300'}>
                                        ★
                                      </span>
                                    ))}
                                    <span className="ml-2 text-sm text-green-700">({orderFeedbacks[order._id].restaurantRating}/5)</span>
                                  </div>
                                </div>
                              )}
                            </div>
                            {orderFeedbacks[order._id].comment && (
                              <div className="mt-3 pt-3 border-t border-green-200">
                                <p className="text-sm text-green-700 font-medium mb-1">Your Comment:</p>
                                <p className="text-green-800">{orderFeedbacks[order._id].comment}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </motion.div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        ) : (
          <motion.div
            className="rounded-2xl border border-dashed border-stone-200 bg-stone-50/50 px-4 py-10 sm:py-12 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-3xl shadow-sm border border-stone-100" aria-hidden>
              📋
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-stone-900 mb-2">
              No order history yet
            </h3>
            <p className="text-sm sm:text-base text-stone-600 max-w-sm mx-auto leading-relaxed">
              {!orderHistoryFetched
                ? 'Enter your 10-digit mobile number above, then tap Load history.'
                : 'No completed orders for this number yet. Finished orders appear here after they are closed.'}
            </p>
          </motion.div>
        )}
      </motion.div>

      {/* Feedback Form Modal */}
      {showFeedbackForm && (
        <FeedbackForm
          orderId={showFeedbackForm}
          order={closedOrders.find(o => o._id === showFeedbackForm)}
          onClose={() => {
            setShowFeedbackForm(null);
          }}
          onSubmitted={async () => {
            const orderId = showFeedbackForm;
            // Immediately check for feedback and update state
            try {
              const response = await axios.get(API_ENDPOINTS.FEEDBACK_GET, {
                params: { orderId }
              });
              setOrderFeedbacks(prev => ({
                ...prev,
                [orderId]: response.data && response.data.length > 0 ? response.data[0] : 'submitted'
              }));
              // Close the feedback form
              setShowFeedbackForm(null);
              // Refresh order history to ensure everything is in sync
              if (onLoadHistory) {
                setTimeout(() => {
                  onLoadHistory();
                }, 500);
              }
            } catch (error) {
              console.error('Failed to refresh feedback:', error);
              // Even if the check fails, mark it as submitted to prevent duplicate submissions
              setOrderFeedbacks(prev => ({
                ...prev,
                [orderId]: 'submitted'
              }));
              setShowFeedbackForm(null);
            }
          }}
        />
      )}
    </div>
  );
};

export default OrderHistory;